import { useStrictLocalization } from "@faire/web/common/localization";
import shuffle from "lodash/shuffle";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSearchSuggestionsQuery } from "@faire/retailer/features/SearchSuggestions-v2/DataFetching/useSearchSuggestionsQuery";
import { setIntervalIfPossible } from "@faire/retailer/lib/async-functions/setIntervalIfPossible";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";

const CYCLING_SPEED = 5000;
const EMPTY_ARRAY: string[] = [];

export const useSearchPlaceholder = () => {
  const [placeholderSuggestion, setPlaceholderSuggestion] = useState<
    string | undefined
  >(undefined);
  const { strictLocalize } = useStrictLocalization();
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const { data } = useSearchSuggestionsQuery("", {
    enabled: isLoggedInRetailer,
  });

  const apparelSearchPlaceholders = useMemo(() => {
    if (!isLoggedInRetailer) {
      return EMPTY_ARRAY;
    }
    return shuffle(data?.search_recommendations).map(({ name }) =>
      strictLocalize(
        {
          defaultMessage: 'Search for "{searchProductName}"',
          description: {
            text: "Suggested search term for the search bar",
          },
        },
        {
          searchProductName: name ?? "",
        }
      )
    );
  }, [data?.search_recommendations, isLoggedInRetailer, strictLocalize]);

  const cycleInterval = useRef<number | undefined>(undefined);
  const currentIndex = useRef(0);
  const emptySuggestions = apparelSearchPlaceholders.length === 0;
  const placeholder = placeholderSuggestion
    ? placeholderSuggestion
    : strictLocalize({
        defaultMessage: "Search products or brands",
        description: {
          text: "Text for search box.",
        },
      });
  const updatePlaceholderSuggestion = useCallback(() => {
    if (emptySuggestions) {
      return;
    }
    setPlaceholderSuggestion(apparelSearchPlaceholders[currentIndex.current]);
    currentIndex.current =
      (currentIndex.current + 1) % apparelSearchPlaceholders.length;
  }, [apparelSearchPlaceholders, emptySuggestions]);

  const startPlaceholderCycling = useCallback(() => {
    if (cycleInterval.current) {
      return;
    }

    updatePlaceholderSuggestion();
    cycleInterval.current = setIntervalIfPossible(
      updatePlaceholderSuggestion,
      CYCLING_SPEED
    );
  }, [updatePlaceholderSuggestion]);

  const stopPlaceholderCycling = useCallback(() => {
    if (cycleInterval.current) {
      clearInterval(cycleInterval.current);
      setPlaceholderSuggestion(undefined);
      cycleInterval.current = undefined;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearInterval(cycleInterval.current);
    };
  });

  return {
    placeholder,
    startPlaceholderCycling,
    stopPlaceholderCycling,
  };
};
